import { FC, lazy, Suspense, useRef, useState } from 'react';

import { H2, PrimaryButton, SecondaryButton, SubBody, TertiaryIconButton, TextField } from '@rikstv/shared-components';

import { ShareLinkSvg } from '../../icons';
import { commonGoogleTagManager } from '../../utils/analytics/googleTagManager';
import { isMobile } from '../../utils/device/device.utils';
import { captureExceptionInSentry } from '../../utils/errorTracker/tracking';
import { quotation } from '../../utils/special-characters/special-characters.utils';
const Modal = lazy(() => import('../modal/Modal').then(module => ({ default: module.Modal })));

import { canUseShareApi, copyToClipboard } from './share.utils';

import styles from './ShareButton.module.css';

export const ContentStackShareButton: FC<{ title: string; shareHref?: string }> = ({ title, shareHref }) => {
  return shareHref ? <ShareButton title={title} shareHref={shareHref} /> : null;
};

type ShareProps =
  | { title: string; type: 'series' | 'movie'; id: string; className?: string }
  | { title: string; shareHref: string; className?: string };

export const ShareButton: FC<ShareProps> = ({ title, className, ...props }) => {
  const [openModal, setOpenModal] = useState(false);

  const url =
    'shareHref' in props
      ? props.shareHref
      : `${window.location.origin}/se/${props.type === 'series' ? 'serie' : 'program'}/${props.id}`;

  const useShareApi = canUseShareApi({ title, url });
  const label = `Del lenke til ${quotation(title)}`;

  const handleClick = async () => {
    try {
      commonGoogleTagManager.clickShareLink(title, url);
      if (useShareApi && isMobile) {
        await navigator.share({ title, url });
      } else {
        setOpenModal(!openModal);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'AbortError') {
          return;
        }
        captureExceptionInSentry(e, { fingerprint: ['share-modal-error'] });
        alert('Noe gikk galt med delingen av ' + url);
      }
    }
  };

  return (
    <div className={className}>
      <TertiaryIconButton
        icon={() => <ShareLinkSvg />}
        onClick={handleClick}
        className={styles.shareBtn}
        title={label}
        aria-label={label}
      />
      <ShareModal openModal={openModal} onClose={() => setOpenModal(false)} url={url} />
    </div>
  );
};

/** Thinner checkmark from figma than the one we have */
const CheckmarkIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.4284 0.384404C17.7684 0.621009 17.8522 1.08843 17.6156 1.42842C17.4634 1.6471 14.4837 5.62335 11.5642 9.5159C10.0995 11.4687 8.64381 13.4087 7.55424 14.8605L5.74175 17.2752C5.74175 17.2752 5.74122 17.2759 5.14144 16.8256L5.74175 17.2752C5.60891 17.4521 5.40418 17.5621 5.18325 17.5744C4.96233 17.5868 4.74724 17.5009 4.59552 17.3399L0.454082 12.9436C0.173254 12.6454 0.183501 12.1772 0.477101 11.8916C0.770701 11.6061 1.23908 11.6089 1.52926 11.8979L1.70508 12.073C1.73726 12.1051 1.76592 12.1393 1.79107 12.1753L5.07647 15.6629L6.35453 13.9601C7.44405 12.5083 8.89969 10.5685 10.3642 8.61588C11.8287 6.66324 13.302 4.69801 14.4269 3.1955C14.9893 2.4442 15.4645 1.80886 15.8078 1.34872C15.9795 1.11859 16.1179 0.932717 16.2176 0.798231C16.2675 0.730925 16.3073 0.677067 16.3366 0.637225L16.3715 0.589469C16.3811 0.576327 16.3844 0.571594 16.3844 0.571594C16.621 0.231608 17.0884 0.1478 17.4284 0.384404Z"
      fill="currentColor"
    />
  </svg>
);

interface ShareModalProps {
  openModal: boolean;
  onClose: () => void;
  url: string;
}

const ShareModal: FC<ShareModalProps> = ({ openModal, onClose, url }) => {
  const [shareSuccess, setShareSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const copyButtonRef = useRef(null);
  const copyButtonText = shareSuccess ? (
    <>
      Kopiert <CheckmarkIcon />
    </>
  ) : (
    'Kopier lenke'
  );

  return (
    <Suspense fallback={null}>
      <Modal
        className={styles.shareModal}
        ref={copyButtonRef}
        open={openModal}
        onClose={() => {
          onClose();
          setShareSuccess(false);
          setErrorMessage(undefined);
        }}
        center>
        <H2>Del lenke</H2>
        <TextField
          name="url"
          label="Lenke"
          className={styles.shareModalInput}
          defaultValue={url}
          readOnly
          onClick={event => selectAllText(event)}
        />
        <div className={styles.shareModalButtonContainer}>
          <PrimaryButton
            ref={copyButtonRef}
            className={styles.shareModalCopyButton}
            onClick={async () => {
              const success = await copyToClipboard(url);
              if (success) {
                setShareSuccess(true);
                setErrorMessage(undefined);
              } else {
                setShareSuccess(false);
                setErrorMessage('Kunne ikke kopiere lenke');
              }
            }}>
            {copyButtonText}
          </PrimaryButton>
          <SecondaryButton
            onClick={() => {
              onClose();
              setShareSuccess(false);
            }}>
            Lukk
          </SecondaryButton>
        </div>
        {errorMessage && <SubBody className={styles.errorMessageContainer}> {errorMessage} </SubBody>}
      </Modal>
    </Suspense>
  );
};

function selectAllText(event: React.MouseEvent<HTMLInputElement>) {
  if (event.target instanceof HTMLInputElement) {
    event.target.select();
  }
}
